import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ConstareAIPanel,
  ConstareAIPanelProvider,
} from '../components/ConstareAIPanel';

/**
 * Lesson 8.2 visual preview at /preview/lesson/8.2
 * "The parts of you that don't get automated"
 * The work AI cannot do for you, and how to invest in it on purpose.
 */

export default function LessonPreviewPage8_2() {
  return (
    <ConstareAIPanelProvider>
      <LessonPage />
    </ConstareAIPanelProvider>
  );
}

const SECTION_IDS = [
  'sec-cold-open',
  'sec-from-last-time',
  'sec-teaching',
  'sec-your-turn',
  'sec-quick-check',
  'sec-lock-card',
];

function LessonPage() {
  const totalSections = 6;
  const [activeIndex, setActiveIndex] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const vis = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);
        if (vis[0]) {
          const idx = SECTION_IDS.indexOf(vis[0].target.id);
          if (idx >= 0) setActiveIndex(idx);
        }
      },
      { rootMargin: '-30% 0px -55% 0px', threshold: [0, 0.25, 0.5, 0.75, 1] }
    );
    SECTION_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  const progressPct = useMemo(
    () => Math.round(((activeIndex + 1) / totalSections) * 100),
    [activeIndex]
  );

  return (
    <div className="relative -mx-6 -my-10 min-h-screen bg-ink">
      <div className="sticky top-0 z-20 border-b border-line bg-ink/95 backdrop-blur">
        <div className="mx-auto flex max-w-content items-center gap-6 px-6 py-3">
          <Link to="/dashboard" className="text-xs text-muted hover:text-text">
            ← Home
          </Link>
          <div className="hidden flex-1 sm:block">
            <div className="flex items-center gap-3">
              <div className="h-1 flex-1 overflow-hidden rounded-full bg-elevated">
                <div
                  className="h-full bg-amber transition-all duration-300"
                  style={{ width: `${progressPct}%` }}
                />
              </div>
              <span className="font-mono text-[10px] uppercase tracking-widest text-muted">
                {activeIndex + 1} / {totalSections}
              </span>
            </div>
          </div>
        </div>
      </div>

      <div className="mx-auto grid max-w-content gap-8 px-6 py-10 lg:grid-cols-[200px_minmax(0,1fr)_380px]">
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <SectionNav activeIndex={activeIndex} />
          </div>
        </aside>
        <article className="min-w-0">
          <LessonHeader />
          <ColdOpen />
          <FromPrev />
          <Teaching />
          <YourTurn />
          <QuickCheck />
          <LockCard />
          <NextUp />
        </article>
        <aside className="hidden lg:block">
          <div className="sticky top-24">
            <ConstareAIPanel />
          </div>
        </aside>
      </div>
    </div>
  );
}

function LessonHeader() {
  return (
    <header className="mb-12">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        Week 8 · AI in a world where it has impacted everything · Lesson 8.2
      </p>
      <h1 className="mt-3 text-4xl font-semibold leading-[1.1] tracking-tight text-text">
        The parts of you that don\'t get automated
      </h1>
      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted">
        <span className="flex items-center gap-1.5">
          <ClockIcon /> 40 min
        </span>
        <span className="text-line">|</span>
        <span>Threads: Judgment · Standing · Relationship</span>
      </div>
      <p className="mt-3 text-xs font-mono uppercase tracking-wider text-amber">
        3 anchor-finding drills
      </p>
    </header>
  );
}

/* --- Section 1: Cold open --- */
function ColdOpen() {
  return (
    <Section id="sec-cold-open" kicker="Cold open" title="A model gets better. Then another. Then another. The question stops being ‘can AI do this part’ and starts being ‘what part is still mine.’">
      <p className="mt-6 text-text/90">
        A year ago there were jobs AI could not do. Write a decent first draft.
        Pull a citation. Code a small feature. Today, with the right tool, AI
        can do all of those at acceptable quality. Next year the list will be
        longer.
      </p>
      <p className="mt-4 text-text/90">
        If you only invest in skills the tools cannot yet do, you spend your
        career sprinting away from a wave. There is a better question. Not
        “what can AI not do.” That answer changes every six months.
        The real question is: what does your work actually need a human for,
        no matter how good the tools get?
      </p>

      <div className="mt-6 rounded-lg border border-line bg-surface p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          The reframe
        </p>
        <p className="mt-3 text-text/90">
          Stop asking what AI cannot do. Start asking what your work cannot
          have a machine do, because the value of the work depends on a
          person being responsible for it.
        </p>
      </div>

      <p className="mt-6 text-text/90">
        Three things show up across almost every kind of work, and they do not
        get easier when the model gets better. Deciding what matters. Standing
        behind the output. Owning the relationship with the people the work is
        for. Those are your anchors.
      </p>
      <p className="mt-4 text-text/90">
        Lesson 8.2 is about naming your anchors and investing in them on
        purpose, so the next model release does not leave you guessing what
        you are still good for.
      </p>
    </Section>
  );
}

/* --- Section 2: From 8.1 --- */
function FromPrev() {
  return (
    <Section id="sec-from-last-time" kicker="From last time · Lesson 8.1" title="8.1 was about reading the field. 8.2 is about what stays yours when the field keeps moving.">
      <p className="mt-6 text-text/90">
        In 8.1 you built an unlock filter: capability, access, workload,
        stability. That filter protects your attention from the noise. But
        even after you filter well, real unlocks do arrive, and they will keep
        eating into what tools can do. The filter is not a fortress. It just
        buys you time to think clearly about what is actually yours.
      </p>
      <p className="mt-4 text-text/90">
        This lesson goes one layer deeper. If the tools keep getting better at
        the mechanical parts, what are the human parts that do not move? Naming
        them is the move that lets you stop being defensive about your
        career and start being intentional about it.
      </p>
      <ol className="mt-6 space-y-4">
        <ReviewItem n="01" q="One sentence on what a capability unlock is, vs. a headline." />
        <ReviewItem n="02" q="Why ‘what can AI not do’ is the wrong question to organize your skills around." />
        <ReviewItem n="03" q="The reframe, in your own words." />
      </ol>
    </Section>
  );
}

function ReviewItem({ n, q }: { n: string; q: string }) {
  return (
    <li className="flex gap-4">
      <span className="font-mono text-sm text-amber pt-1">{n}.</span>
      <span className="text-text/90" dangerouslySetInnerHTML={{ __html: q }} />
    </li>
  );
}

/* --- Section 3: Teaching --- */
function Teaching() {
  return (
    <Section id="sec-teaching" kicker="Teaching · The three anchors" title="Three pieces of your work that do not get automated, because the value depends on a person being responsible for them.">
      <Subhead n={1} title="The vocab for the lesson" />
      <VocabCard
        term="Anchor"
        definition="A part of your work whose value depends on a person being responsible for it. Anchors do not get easier when the AI gets better. They are the pieces a teammate, a customer, a teacher, or a board would want a named human standing behind, no matter what tools were used to produce the output."
        analogy="An anchor on a boat is not the engine. The engine moves the boat. The anchor is what keeps the boat from drifting when the current changes. AI is your engine: it does the moving. Anchors are what keep your work from drifting into whatever the model would have produced if no one was there. The engine gets better every year. The anchor still has to hold."
        memoryHook="An anchor is where a name has to go. Where the work needs a human to be responsible, not just present. If you can remove the human and the value is unchanged, it was not an anchor."
        why="Skills you organize around ‘things AI cannot do yet’ expire on the AI release schedule. Anchors do not. Investing in your anchors is how you build a career that compounds across model generations instead of resetting every time the tools get better. It is also how you stay calm when the headlines say everything is changing. Most of what is changing is the engine. Your anchors do not move."
      />

      <Subhead n={2} title="The three anchors" className="mt-12" />
      <p className="mt-4 text-text/90">
        Almost every kind of work has these three. Different jobs weight them
        differently. None of them disappear when the model gets better.
      </p>
      <AnchorsGrid />

      <Subhead n={3} title="How anchors show up in real work" className="mt-12" />
      <p className="mt-4 text-text/90">
        Anchors do not look impressive in the moment. They look like a
        question asked at the right time, a sentence rewritten because the
        first version was technically right and humanly wrong, a phone call
        before an email. Small moves. They are the difference between work
        that lands and work that gets ignored.
      </p>
      <ExamplesGrid />

      <Subhead n={4} title="Where AI helps and where AI hurts on anchors" className="mt-12" />
      <p className="mt-4 text-text/90">
        AI is great around your anchors. It can prep, draft, summarize, and
        rehearse. It cannot be the anchor. The moment you try to make it the
        anchor, the work loses the thing that made it valuable.
      </p>
      <SplitGrid />

      <Subhead n={5} title="The anchor audit" className="mt-12" />
      <p className="mt-4 text-text/90">
        Once a quarter, take one real piece of work you shipped. Mark the
        anchor moments: where did the judgment, the standing, or the
        relationship matter most? Then ask the harder question: did you
        actually invest in those moments, or did you let the AI carry them?
        The anchor audit is how you keep yourself honest as the tools keep
        improving.
      </p>
      <div className="mt-6 rounded-lg border border-emerald-500/40 bg-emerald-500/[0.04] p-5">
        <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
          The rule
        </p>
        <p className="mt-3 text-text/90">
          AI does the engine work. You do the anchor work. If your anchor
          moments are the ones you skipped, the model is doing your job, and
          your career is on autopilot.
        </p>
      </div>
    </Section>
  );
}

const ANCHORS = [
  {
    n: '01',
    name: 'Judgment',
    body: 'Deciding what matters in the situation in front of you. Which problem to actually solve. Which detail is load-bearing. Which trade-off is the right one for this team, this customer, this moment. AI can generate options. It cannot decide that this is the option, because that decision is responsible to facts and stakes only you can see.',
  },
  {
    n: '02',
    name: 'Standing',
    body: 'Putting your name on the work and being able to defend any line in it. Standing is not bravado. It is the willingness to be the named human if the work is questioned, criticized, or relied on. AI can produce the artifact. It cannot stand behind it. The teammate, customer, or audience is asking for a person, not a model.',
  },
  {
    n: '03',
    name: 'Relationship',
    body: 'Owning the human connection with the people the work is for. Reading the room, choosing a tone, knowing what is unsaid, knowing when to call instead of email. The work has an audience. The audience is a person or a group of people. AI can draft. It cannot be the relationship.',
  },
];

function AnchorsGrid() {
  return (
    <div className="mt-6 space-y-4">
      {ANCHORS.map((p) => (
        <div key={p.n} className="rounded-lg border border-amber/40 bg-amber/[0.04] p-5">
          <div className="flex flex-wrap items-baseline gap-3">
            <span className="font-mono text-xs text-muted">Anchor {p.n}</span>
            <h4 className="text-lg font-semibold text-amber">{p.name}</h4>
          </div>
          <p className="mt-3 text-text/90">{p.body}</p>
        </div>
      ))}
    </div>
  );
}

const EXAMPLES = [
  {
    role: 'High school newspaper editor',
    judgment: 'Deciding which of three drafted angles will actually land with the student body this month.',
    standing: 'Putting your name on the editorial. If a teacher pushes back, you can defend every sentence.',
    relationship: 'Texting the writer in private before publishing to flag one line you changed and why.',
  },
  {
    role: 'College capstone team lead',
    judgment: 'Choosing which scope cut to make when the project is two weeks behind. AI can list options. You pick.',
    standing: 'Owning the email to the faculty advisor that says the timeline slipped, and what you are doing about it.',
    relationship: 'Knowing which teammate is quietly burning out and adjusting the workload before they ask.',
  },
  {
    role: 'Recent grad on a marketing team',
    judgment: 'Deciding the brief that goes to the founder is wrong, even though the AI draft was polished.',
    standing: 'Signing the launch memo. If the campaign underperforms, you are the named human in the post-mortem.',
    relationship: 'Walking over to the designer before the deadline instead of sending the seventh Slack thread.',
  },
];

function ExamplesGrid() {
  return (
    <div className="mt-6 space-y-4">
      {EXAMPLES.map((row) => (
        <div key={row.role} className="rounded-lg border border-line bg-surface p-5">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            {row.role}
          </p>
          <div className="mt-3 grid gap-3 lg:grid-cols-3">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-wider text-amber">Judgment</p>
              <p className="mt-1 text-sm text-text/90">{row.judgment}</p>
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-wider text-amber">Standing</p>
              <p className="mt-1 text-sm text-text/90">{row.standing}</p>
            </div>
            <div>
              <p className="font-mono text-[10px] uppercase tracking-wider text-amber">Relationship</p>
              <p className="mt-1 text-sm text-text/90">{row.relationship}</p>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

const SPLIT = [
  {
    side: 'AI helps',
    items: [
      'Generating options so you have something concrete to apply judgment to.',
      'Drafting the artifact you will then stand behind, edit, and own.',
      'Rehearsing a hard conversation before you actually have the relationship moment.',
      'Surfacing things you might have missed, so you do not anchor on incomplete information.',
    ],
    accent: 'emerald' as const,
  },
  {
    side: 'AI hurts',
    items: [
      'Making the judgment call for you: it will pick the most-likely answer, not the right answer for this room.',
      'Standing in for you: when the work is questioned, the teammate or customer wants a person, not a model.',
      'Being the relationship: a message that was supposed to come from you, written entirely by AI, is felt.',
      'Letting you skip the anchor moments because the draft &lsquo;already looks done.&rsquo;',
    ],
    accent: 'rose' as const,
  },
];

function SplitGrid() {
  return (
    <div className="mt-6 grid gap-4 lg:grid-cols-2">
      {SPLIT.map((col) => {
        const isEm = col.accent === 'emerald';
        const borderCls = isEm ? 'border-emerald-500/40' : 'border-rose-500/40';
        const bgCls = isEm ? 'bg-emerald-500/[0.04]' : 'bg-rose-500/[0.04]';
        const labelCls = isEm ? 'text-emerald-300' : 'text-rose-300';
        return (
          <div key={col.side} className={`rounded-lg border ${borderCls} ${bgCls} p-5`}>
            <p className={`font-mono text-[11px] uppercase tracking-wider ${labelCls}`}>
              {col.side}
            </p>
            <ul className="mt-3 space-y-2">
              {col.items.map((it, i) => (
                <li key={i} className="flex gap-2 text-sm text-text/90">
                  <span className={`shrink-0 ${labelCls}`}>·</span>
                  <span dangerouslySetInnerHTML={{ __html: it }} />
                </li>
              ))}
            </ul>
          </div>
        );
      })}
    </div>
  );
}

/* --- Section 4: Your turn --- */
function YourTurn() {
  return (
    <Section id="sec-your-turn" kicker="Your turn · Find the anchor" title="Three scenes. Each one has one anchor that matters most. Pick the one the situation depends on.">
      <p className="mt-6 text-text/90">
        For each scene, two anchors are present. One is what the situation
        actually depends on. Pick the anchor whose absence would change the
        outcome.
      </p>
      <div className="mt-8 space-y-6">
        {DRILLS.map((d) => (
          <AnchorDrill key={d.n} drill={d} />
        ))}
      </div>
    </Section>
  );
}

type DrillSpec = {
  n: string;
  scene: string;
  optionA: string;
  optionB: string;
  correct: 'A' | 'B';
  verdict: string;
};

const DRILLS: DrillSpec[] = [
  {
    n: '01',
    scene:
      'You are leading a college capstone group. The AI-drafted version of the project brief is polished and reads well. Reading it carefully, you realize the scope it commits to will not finish on time. Three teammates already replied &ldquo;looks good.&rdquo;',
    optionA: 'Standing: you need to sign your name on it.',
    optionB: 'Judgment: the polished draft commits to a scope that will fail.',
    correct: 'B',
    verdict:
      'Standing matters here too, but the moment that changes the outcome is the judgment call: the AI produced a confident draft that is wrong on the most important variable, scope. If you ship what is in front of you, the project misses. The anchor move is to override the polished draft with your read of the situation, write the scope cut, and bring it back to the team. Standing comes after, when you defend the cut.',
  },
  {
    n: '02',
    scene:
      'You are a recent grad on a sales team. AI drafts a thoughtful follow-up email to a prospect who went quiet after a tough demo last week. The email is solid. You also remember the prospect mentioned, in passing, that her dog had surgery the week before.',
    optionA: 'Relationship: a real follow-up references the dog, not the demo.',
    optionB: 'Standing: send the AI draft and own the result.',
    correct: 'A',
    verdict:
      'The AI draft is fine. A real follow-up from a real person mentions the dog, briefly and naturally, before getting to the work. That is not a hack. It is the actual relationship doing the work the email cannot do on its own. The AI did not hear the offhand comment. You did. The anchor move is the small, specific human detail that proves a person, not a model, is at the other end of the inbox.',
  },
  {
    n: '03',
    scene:
      'You are editing the high school newspaper. AI helped you draft an editorial criticizing a recent school policy. Every fact checks out. A staff writer asks: &ldquo;Are we actually ready to defend every line of this if the principal calls a meeting?&rdquo;',
    optionA: 'Judgment: are the arguments well-reasoned enough.',
    optionB: 'Standing: are you ready to be the named human if this is questioned.',
    correct: 'B',
    verdict:
      'Judgment is in the work already, the arguments check out. The question on the table is standing: who is the named human who can defend any line in the editorial if the principal pulls you into a room. AI did not write this. The newspaper did, which means you did. The anchor move is to read every line one more time with the question &ldquo;can I defend this if asked&rdquo; and either commit or change it. Then ship with your name.',
  },
];

function AnchorDrill({ drill }: { drill: DrillSpec }) {
  const [picked, setPicked] = useState<'A' | 'B' | null>(null);
  const wasWrong = picked !== null && picked !== drill.correct;

  return (
    <div className="rounded-lg border border-line bg-surface p-5">
      <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
        Drill {drill.n} · The scene
      </p>
      <p
        className="mt-3 text-text/90"
        dangerouslySetInnerHTML={{ __html: drill.scene }}
      />

      <div className="mt-5 border-t border-line pt-4">
        <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
          Which anchor does this scene depend on?
        </p>
        <div className="mt-3 grid gap-3 lg:grid-cols-2">
          {(['A', 'B'] as const).map((key) => {
            const text = key === 'A' ? drill.optionA : drill.optionB;
            const isPicked = picked === key;
            const isCorrect = key === drill.correct;
            const shouldShowCorrect = picked !== null && isCorrect;
            const shouldShowWrong = isPicked && !isCorrect;
            let cls =
              'rounded-md border border-line bg-elevated p-4 text-left text-sm text-text/85 hover:border-amber transition-colors';
            if (shouldShowCorrect)
              cls =
                'rounded-md border border-emerald-500 bg-emerald-500/[0.08] p-4 text-left text-sm text-text';
            if (shouldShowWrong)
              cls =
                'rounded-md border border-rose-500 bg-rose-500/[0.08] p-4 text-left text-sm text-text';
            return (
              <button
                key={key}
                className={cls}
                onClick={() => setPicked(key)}
              >
                <span className="font-mono text-[10px] uppercase tracking-wider text-muted block">
                  Option {key}
                </span>
                <span className="mt-2 block" dangerouslySetInnerHTML={{ __html: text }} />
              </button>
            );
          })}
        </div>

        {wasWrong && (
          <div className="mt-4 rounded-md border border-rose-500/40 bg-rose-500/[0.06] p-4">
            <p className="font-mono text-[11px] uppercase tracking-wider text-rose-300">
              Not this one · Option {picked}
            </p>
            <p className="mt-2 text-sm text-text/85">
              That anchor is present, but it is not the one the situation
              actually depends on. Ask: which anchor, if missing, would
              change the outcome?
            </p>
          </div>
        )}

        {picked !== null && (
          <div className="mt-4 rounded-md border border-emerald-500/40 bg-emerald-500/[0.04] p-4">
            <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
              {wasWrong ? 'The anchor the situation depends on' : 'Correct'} · Option {drill.correct}
            </p>
            <p className="mt-2 text-sm text-text/90" dangerouslySetInnerHTML={{ __html: drill.verdict }} />
          </div>
        )}
      </div>
    </div>
  );
}

/* --- Section 5: Quick check --- */
function QuickCheck() {
  return (
    <Section id="sec-quick-check" kicker="Quick check" title="Five questions. Answer first. Then reveal.">
      <div className="mt-6 space-y-4">
        <QuickCheckItem n="Q1" q="Define Anchor in one sentence." answer="A part of your work whose value depends on a person being responsible for it; it does not get easier when the AI gets better." />
        <QuickCheckItem n="Q2" q="The three anchors." answer="Judgment (deciding what matters), Standing (being the named human behind the work), Relationship (owning the human connection with the people the work is for)." />
        <QuickCheckItem n="Q3" q="Why ‘what can AI not do’ is the wrong question." answer="The answer changes every six months. Skills built on that question expire on the AI release schedule. Anchors do not." />
        <QuickCheckItem n="Q4" q="Two things AI helps with on anchors. Two things AI hurts." answer="Helps: generating options, drafting artifacts you stand behind, rehearsing hard conversations, surfacing things you might miss. Hurts: making judgment calls for you, standing in for you, being the relationship, letting you skip anchor moments because the draft looks done." />
        <QuickCheckItem n="Q5" q="What is the anchor audit?" answer="Once a quarter, take one real piece of work and mark the anchor moments. Ask whether you actually invested in them or let the AI carry them." />
      </div>
    </Section>
  );
}

/* --- Section 6: Lock card --- */
function LockCard() {
  return (
    <Section id="sec-lock-card" kicker="Lock card" title="Screenshot this. The three anchors.">
      <div className="mt-6 rounded-2xl border border-amber/40 bg-gradient-to-b from-amber/[0.06] to-transparent p-7" data-lockcard-body>
        <div className="flex items-start justify-between">
          <div>
            <p className="font-mono text-[11px] uppercase tracking-wider text-amber">
              Lesson 8.2 · Lock card · The three anchors
            </p>
          </div>
          <button className="font-mono text-[10px] uppercase tracking-wider text-muted hover:text-text">
            ↓ Save image
          </button>
        </div>
        <h3 className="mt-4 text-2xl font-semibold leading-snug text-text">
          AI gets better at the engine work every year. The anchor work is
          yours, and it does not move.
        </h3>

        <div className="mt-6 grid gap-3 sm:grid-cols-3">
          <LockTile n="01" name="Judgment" body="Deciding what matters in the situation in front of you." />
          <LockTile n="02" name="Standing" body="Being the named human behind the work, defending any line." />
          <LockTile n="03" name="Relationship" body="Owning the human connection with the people the work is for." />
        </div>

        <div className="mt-6 rounded-md border border-line bg-surface p-4">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            The test
          </p>
          <p className="mt-2 text-text/90">
            If you can remove the human and the value of the work is
            unchanged, it was not an anchor.
          </p>
        </div>

        <div className="mt-4 rounded-md border border-emerald-500/40 bg-emerald-500/[0.04] p-4">
          <p className="font-mono text-[11px] uppercase tracking-wider text-emerald-300">
            The anchor audit
          </p>
          <p className="mt-2 text-text/90">
            Once a quarter, take one real piece of work. Mark the anchor
            moments. Ask whether you invested in them or let the AI carry
            them.
          </p>
        </div>

        <p className="mt-6 text-sm text-muted italic">
          The engine moves the boat. The anchor keeps it where you meant to
          stand.
        </p>
      </div>
    </Section>
  );
}

function LockTile({ n, name, body }: { n: string; name: string; body: string }) {
  return (
    <div className="rounded-lg border border-line bg-surface p-4">
      <div className="flex items-baseline gap-2">
        <span className="font-mono text-[10px] text-muted">{n}</span>
        <span className="rounded bg-amber/15 px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-amber">
          {name}
        </span>
      </div>
      <p className="mt-3 text-sm text-text/85">{body}</p>
    </div>
  );
}

/* --- Next up --- */
function NextUp() {
  return (
    <div className="mt-14 rounded-xl border border-line bg-surface p-6" data-nextup-card>
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="max-w-xl">
          <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
            Up next · Lesson 8.3
          </p>
          <h3 className="mt-2 text-xl font-semibold text-text">
            Drift: staying sharp on purpose
          </h3>
          <p className="mt-3 text-sm text-text/85">
            How good students quietly stop thinking when the AI gets good
            enough. Vocab: Drift. And the recovery move: one task a week,
            done without AI on purpose, to re-prove your judgment still
            works without the scaffolding.
          </p>
        </div>
        <button className="rounded-md border border-amber/40 px-4 py-2 text-sm font-medium text-amber hover:bg-amber/[0.08]">
          Coming soon
        </button>
      </div>
    </div>
  );
}

/* --- Primitives --- */

function Section({
  id,
  kicker,
  title,
  children,
}: {
  id: string;
  kicker: string;
  title: string;
  children: React.ReactNode;
}) {
  return (
    <section id={id} className="mt-16 scroll-mt-24">
      <p className="font-mono text-[11px] uppercase tracking-[0.18em] text-amber">
        {kicker}
      </p>
      <h2 className="mt-3 text-2xl font-semibold leading-snug text-text" dangerouslySetInnerHTML={{ __html: title }} />
      <div>{children}</div>
    </section>
  );
}

function Subhead({ n, title, className = '' }: { n: number; title: string; className?: string }) {
  return (
    <h3 className={`text-xl font-semibold text-text ${className}`}>
      <span className="mr-3 font-mono text-sm text-amber">{n}</span>
      {title}
    </h3>
  );
}

function VocabCard({
  term,
  definition,
  analogy,
  memoryHook,
  why,
}: {
  term: string;
  definition: string;
  analogy: string;
  memoryHook: string;
  why: string;
}) {
  const [revealed, setRevealed] = useState(false);
  return (
    <div className="mt-6 rounded-xl border border-amber/40 bg-amber/[0.04] p-6">
      <p className="font-mono text-[11px] uppercase tracking-wider text-muted">
        Vocab · 8.2 · New term
      </p>
      <h4 className="mt-2 text-2xl font-semibold text-text">{term}</h4>
      {!revealed ? (
        <div className="mt-5">
          <p className="text-sm text-text/85">
            Write your own one-sentence definition first. Then reveal.
          </p>
          <textarea
            className="mt-3 w-full rounded-md border border-line bg-elevated p-3 text-sm text-text placeholder:text-muted focus:border-amber focus:outline-none"
            rows={3}
            placeholder={`Your definition of "${term}"...`}
          />
          <button
            className="mt-3 rounded-md bg-amber px-4 py-2 text-sm font-medium text-ink hover:opacity-90"
            onClick={() => setRevealed(true)}
          >
            Reveal definition →
          </button>
        </div>
      ) : (
        <div className="mt-5 space-y-4">
          <VocabBlock label="Definition" body={definition} />
          <VocabBlock label="Analogy" body={analogy} accent />
          <VocabBlock label="Memory hook" body={memoryHook} />
          <VocabBlock label="Why it matters" body={why} />
        </div>
      )}
    </div>
  );
}

function VocabBlock({ label, body, accent = false }: { label: string; body: string; accent?: boolean }) {
  return (
    <div
      className={`rounded-md border ${accent ? 'border-amber/40 bg-amber/[0.04]' : 'border-line bg-surface'} p-4`}
    >
      <p className={`font-mono text-[10px] uppercase tracking-wider ${accent ? 'text-amber' : 'text-muted'}`}>
        {label}
      </p>
      <p className="mt-2 text-sm text-text/90">{body}</p>
    </div>
  );
}

function QuickCheckItem({ n, q, answer }: { n: string; q: string; answer: string }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="rounded-lg border border-line bg-surface p-4">
      <div className="flex items-start justify-between gap-4">
        <div className="flex gap-3">
          <span className="font-mono text-xs text-amber pt-0.5">{n}</span>
          <p className="text-text/90" dangerouslySetInnerHTML={{ __html: q }} />
        </div>
        <button
          onClick={() => setOpen(!open)}
          className="shrink-0 rounded-md border border-line px-3 py-1 text-xs text-muted hover:border-amber hover:text-text"
        >
          {open ? 'Hide' : 'Reveal'}
        </button>
      </div>
      {open && (
        <p
          className="mt-3 border-t border-line pt-3 text-sm text-text/85"
          dangerouslySetInnerHTML={{ __html: answer }}
        />
      )}
    </div>
  );
}

function SectionNav({ activeIndex }: { activeIndex: number }) {
  const items = [
    'Cold open',
    'From 8.1',
    'The three anchors',
    'Your turn',
    'Quick check',
    'Lock card',
  ];
  return (
    <nav>
      <p className="font-mono text-[10px] uppercase tracking-widest text-muted">
        In this lesson
      </p>
      <ol className="mt-4 space-y-1">
        {items.map((label, i) => {
          const isActive = i === activeIndex;
          const isPast = i < activeIndex;
          return (
            <li key={label}>
              <a
                href={`#${SECTION_IDS[i]}`}
                className={`flex items-center gap-3 rounded-md px-3 py-2 text-sm transition-colors ${
                  isActive
                    ? 'bg-elevated text-text'
                    : 'text-muted hover:text-text'
                }`}
              >
                <span
                  className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-mono ${
                    isActive
                      ? 'bg-amber text-ink'
                      : isPast
                      ? 'border border-amber/60 text-amber'
                      : 'border border-line text-muted'
                  }`}
                >
                  {isPast ? '✓' : i + 1}
                </span>
                <span>{label}</span>
              </a>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}

function ClockIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="text-muted">
      <circle cx="12" cy="12" r="10" />
      <polyline points="12 6 12 12 16 14" />
    </svg>
  );
}
